import { BLOCK_POSITION, CraneParameters } from "../../types";
import { AlertDefinitionKey } from "../../data/keys";
import { createAlertFromDefinition } from "../../data/alerts";
import { calculateMHRadius, calculateSWL } from "../../utils/craneHelpers";

export type CurrentAction =
  | "Hoisting"
  | "Lowering"
  | "Booming up"
  | "Booming down"
  | "Slewing cw"
  | "Slewing ccw"
  | "Idle";

export interface CraneSlice {
  // State
  craneParameters: CraneParameters;
  currentAction: CurrentAction;
  lastSlewChangeAt: number;
  lastBoomChangeAt: number;
  lastHoistChangeAt: number;

  // Actions
  resetCraneParameters: () => void;
  updateCraneParameter: <K extends keyof CraneParameters>(
    key: K,
    value: CraneParameters[K]
  ) => void;
  incrementCraneParameter: <K extends keyof CraneParameters>(
    key: K,
    amount?: number
  ) => void;
  decrementCraneParameter: <K extends keyof CraneParameters>(
    key: K,
    amount?: number
  ) => void;
}

// Derive block position from absolute travel (in meters)
const computeBlockPosition = (absTravel: number): BLOCK_POSITION => {
  if (absTravel <= 3) return BLOCK_POSITION.HIGH;
  if (absTravel <= 6) return BLOCK_POSITION.PRE_HIGH;
  return BLOCK_POSITION.MID_RANGE;
};

const DEFAULT_SPEED_MIN = -25;
const DEFAULT_SPEED_MAX = 25;
const PRE_HIGH_SPEED_MIN = -10;
const PRE_HIGH_SPEED_MAX = 10;
// Initial crane parameters
const initialCraneParameters: CraneParameters = {
  swl: 3026.3,
  swlPercentage: 50.9,
  speed: 0.0,
  speedMin: -25.0,
  speedMax: 25.0,
  boomSpeed: 0.0,
  slewSpeed: 0.0,
  mhLoad: 1150.0,
  maxRadius: 276.8,
  blockPosition: BLOCK_POSITION.MID_RANGE,
  absTravel: 10.0,
  relTravel: 10.0,
  windSpeed: 16,
  slewAngle: 0,
  boomAngle: 55.0,
  boomMaxAngle: 80,
  boomMinAngle: 40,
  slewMaxAngle: 70,
  slewMinAngle: -70,
};

export const selectMHRadius = (state: CraneSlice) => calculateMHRadius(state);
export const selectSWL = (state: CraneSlice) => calculateSWL(state);

const deriveCurrentAction = (
  params: CraneParameters,
  meta: {
    lastSlewChangeAt: number;
    lastBoomChangeAt: number;
    lastHoistChangeAt: number;
  }
): CurrentAction => {
  const candidates: { label: CurrentAction; at: number }[] = [];
  if ((params.slewSpeed || 0) !== 0) {
    candidates.push({
      label: (params.slewSpeed as number) > 0 ? "Slewing cw" : "Slewing ccw",
      at: meta.lastSlewChangeAt,
    });
  }
  if ((params.boomSpeed || 0) !== 0) {
    candidates.push({
      label: (params.boomSpeed as number) > 0 ? "Booming up" : "Booming down",
      at: meta.lastBoomChangeAt,
    });
  }
  if (params.speed !== 0) {
    candidates.push({
      label: params.speed > 0 ? "Hoisting" : "Lowering",
      at: meta.lastHoistChangeAt,
    });
  }
  if (candidates.length === 0) return "Idle";
  candidates.sort((a, b) => b.at - a.at);
  return candidates[0].label as CurrentAction;
};
export const createCraneSlice = (set: any, get: any): CraneSlice => ({
  // Initial state
  craneParameters: initialCraneParameters,
  currentAction: "Idle",
  lastSlewChangeAt: 0,
  lastBoomChangeAt: 0,
  lastHoistChangeAt: 0,

  // Actions
  resetCraneParameters: () =>
    set(() => ({
      craneParameters: initialCraneParameters,
    })),
  updateCraneParameter: <K extends keyof CraneParameters>(
    key: K,
    value: CraneParameters[K]
  ) =>
    set((state: any) => {
      const p = state.craneParameters;
      let next = value as any;
      if (key === "boomAngle") {
        next = Math.max(p.boomMinAngle, Math.min(p.boomMaxAngle, value as any));
      }
      if (key === "slewAngle") {
        next = Math.max(p.slewMinAngle, Math.min(p.slewMaxAngle, value as any));
      }
      if (key === "speed") {
        next = Math.max(p.speedMin, Math.min(p.speedMax, value as any));
      }
      if (key === "boomSpeed") {
        next = Math.max(p.speedMin, Math.min(p.speedMax, value as any));
      }
      if (key === "slewSpeed") {
        next = Math.max(p.speedMin, Math.min(p.speedMax, value as any));
      }
      if (key === "absTravel") {
        const nextBlock = computeBlockPosition(next);

        // Adjust speed limits based on block position
        let nextSpeedMin = p.speedMin;
        let nextSpeedMax = p.speedMax;
        if (nextBlock === BLOCK_POSITION.HIGH) {
          nextSpeedMin = PRE_HIGH_SPEED_MIN;
          nextSpeedMax = 0;
        } else if (nextBlock === BLOCK_POSITION.PRE_HIGH) {
          nextSpeedMin = PRE_HIGH_SPEED_MIN;
          nextSpeedMax = PRE_HIGH_SPEED_MAX;
        } else {
          nextSpeedMin = DEFAULT_SPEED_MIN;
          nextSpeedMax = DEFAULT_SPEED_MAX;
        }
        let nextSpeed = Math.max(nextSpeedMin, Math.min(nextSpeedMax, p.speed));

        // Maintain alerts presence based on current block position
        try {
          const { addAlert, removeAlert, alerts } = get();
          const removeByDefinition = (def: AlertDefinitionKey) => {
            alerts
              .filter((a: any) => a.definitionId === def)
              .forEach((a: any) => removeAlert(a.id));
          };
          const ensurePresent = (def: AlertDefinitionKey) => {
            const exists = alerts.some((a: any) => a.definitionId === def);
            if (!exists) {
              const base = createAlertFromDefinition(def);
              addAlert({
                id: `alert-${Date.now()}-${Math.random()
                  .toString(36)
                  .slice(2)}`,
                ...base,
              });
            }
          };

          if (nextBlock === BLOCK_POSITION.HIGH) {
            ensurePresent(AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_HIGH);
            removeByDefinition(
              AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_PRE_HIGH
            );
          } else if (nextBlock === BLOCK_POSITION.PRE_HIGH) {
            ensurePresent(
              AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_PRE_HIGH
            );
            removeByDefinition(
              AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_HIGH
            );
          } else {
            removeByDefinition(
              AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_HIGH
            );
            removeByDefinition(
              AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_PRE_HIGH
            );
          }
        } catch {}

        return {
          craneParameters: {
            ...p,
            absTravel: next,
            blockPosition: nextBlock,
            speedMin: nextSpeedMin,
            speedMax: nextSpeedMax,
            speed: nextSpeed,
          },
          // Do not update last* timestamps here (not a user interaction). Still derive current action.
          currentAction: deriveCurrentAction(
            {
              ...p,
              absTravel: next,
              blockPosition: nextBlock,
              speedMin: nextSpeedMin,
              speedMax: nextSpeedMax,
              speed: nextSpeed,
            },
            {
              lastSlewChangeAt: state.lastSlewChangeAt,
              lastBoomChangeAt: state.lastBoomChangeAt,
              lastHoistChangeAt: state.lastHoistChangeAt,
            }
          ),
        };
      }
      if (key === "speedMin") {
        const rawMin = Math.max(-100, Math.min(100, value as any));
        const newMin = Math.min(rawMin, p.speedMax);
        const clampedSpeed = Math.max(newMin, Math.min(p.speedMax, p.speed));
        return {
          craneParameters: {
            ...p,
            speedMin: newMin,
            speed: clampedSpeed,
          },
          currentAction: deriveCurrentAction(
            { ...p, speedMin: newMin, speed: clampedSpeed },
            {
              lastSlewChangeAt: state.lastSlewChangeAt,
              lastBoomChangeAt: state.lastBoomChangeAt,
              lastHoistChangeAt: state.lastHoistChangeAt,
            }
          ),
        };
      }
      if (key === "speedMax") {
        const rawMax = Math.max(-100, Math.min(100, value as any));
        const newMax = Math.max(rawMax, p.speedMin);
        const clampedSpeed = Math.max(p.speedMin, Math.min(newMax, p.speed));
        return {
          craneParameters: {
            ...p,
            speedMax: newMax,
            speed: clampedSpeed,
          },
          currentAction: deriveCurrentAction(
            { ...p, speedMax: newMax, speed: clampedSpeed },
            {
              lastSlewChangeAt: state.lastSlewChangeAt,
              lastBoomChangeAt: state.lastBoomChangeAt,
              lastHoistChangeAt: state.lastHoistChangeAt,
            }
          ),
        };
      }
      const now = Date.now();
      let lastSlewChangeAt = state.lastSlewChangeAt;
      let lastBoomChangeAt = state.lastBoomChangeAt;
      let lastHoistChangeAt = state.lastHoistChangeAt;
      if (key === "slewSpeed" && next !== p.slewSpeed) {
        lastSlewChangeAt = now;
      }
      if (key === "boomSpeed" && next !== p.boomSpeed) {
        lastBoomChangeAt = now;
      }
      if (key === "speed" && next !== p.speed) {
        lastHoistChangeAt = now;
      }
      const nextParams = { ...p, [key]: next } as CraneParameters;
      return {
        craneParameters: nextParams,
        lastSlewChangeAt,
        lastBoomChangeAt,
        lastHoistChangeAt,
        currentAction: deriveCurrentAction(nextParams, {
          lastSlewChangeAt,
          lastBoomChangeAt,
          lastHoistChangeAt,
        }),
      };
    }),

  incrementCraneParameter: <K extends keyof CraneParameters>(
    key: K,
    amount: number = 1
  ) =>
    set((state: any) => {
      const p = state.craneParameters;
      let next = (p[key] as any) + amount;
      if (key === "boomAngle") {
        next = Math.max(p.boomMinAngle, Math.min(p.boomMaxAngle, next));
      }
      if (key === "slewAngle") {
        next = Math.max(p.slewMinAngle, Math.min(p.slewMaxAngle, next));
      }
      if (key === "speed") {
        next = Math.max(p.speedMin, Math.min(p.speedMax, next));
      }
      if (key === "boomSpeed") {
        next = Math.max(p.speedMin, Math.min(p.speedMax, next));
      }
      if (key === "slewSpeed") {
        next = Math.max(p.speedMin, Math.min(p.speedMax, next));
      }
      if (key === "absTravel") {
        const nextBlock = computeBlockPosition(next);

        // Adjust speed limits based on block position
        let nextSpeedMin = p.speedMin;
        let nextSpeedMax = p.speedMax;
        if (nextBlock === BLOCK_POSITION.HIGH) {
          nextSpeedMin = DEFAULT_SPEED_MIN;
          nextSpeedMax = 0;
        } else if (nextBlock === BLOCK_POSITION.PRE_HIGH) {
          nextSpeedMin = PRE_HIGH_SPEED_MIN;
          nextSpeedMax = PRE_HIGH_SPEED_MAX;
        } else {
          nextSpeedMin = DEFAULT_SPEED_MIN;
          nextSpeedMax = DEFAULT_SPEED_MAX;
        }
        let nextSpeed = Math.max(nextSpeedMin, Math.min(nextSpeedMax, p.speed));

        try {
          const { addAlert, removeAlert, alerts } =
            (state as any).get?.() ?? get();
          const removeByDefinition = (def: AlertDefinitionKey) => {
            alerts
              .filter((a: any) => a.definitionId === def)
              .forEach((a: any) => removeAlert(a.id));
          };

          const ensurePresent = (def: AlertDefinitionKey) => {
            const exists = alerts.some((a: any) => a.definitionId === def);
            if (!exists) {
              const base = createAlertFromDefinition(def);
              addAlert({
                id: `alert-${Date.now()}-${Math.random()
                  .toString(36)
                  .slice(2)}`,
                ...base,
              });
            }
          };

          if (nextBlock === BLOCK_POSITION.HIGH) {
            ensurePresent(AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_HIGH);
            removeByDefinition(
              AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_PRE_HIGH
            );
          } else if (nextBlock === BLOCK_POSITION.PRE_HIGH) {
            ensurePresent(
              AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_PRE_HIGH
            );
            removeByDefinition(
              AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_HIGH
            );
          } else {
            removeByDefinition(
              AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_HIGH
            );
            removeByDefinition(
              AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_PRE_HIGH
            );
          }
        } catch {}

        return {
          craneParameters: {
            ...p,
            absTravel: next,
            blockPosition: nextBlock,
            speedMin: nextSpeedMin,
            speedMax: nextSpeedMax,
            speed: nextSpeed,
          },
          currentAction: deriveCurrentAction(
            {
              ...p,
              absTravel: next,
              blockPosition: nextBlock,
              speedMin: nextSpeedMin,
              speedMax: nextSpeedMax,
              speed: nextSpeed,
            },
            {
              lastSlewChangeAt: state.lastSlewChangeAt,
              lastBoomChangeAt: state.lastBoomChangeAt,
              lastHoistChangeAt: state.lastHoistChangeAt,
            }
          ),
        };
      }
      const now = Date.now();
      let lastSlewChangeAt = state.lastSlewChangeAt;
      let lastBoomChangeAt = state.lastBoomChangeAt;
      let lastHoistChangeAt = state.lastHoistChangeAt;
      if (key === "slewSpeed" && next !== p.slewSpeed) {
        lastSlewChangeAt = now;
      }
      if (key === "boomSpeed" && next !== p.boomSpeed) {
        lastBoomChangeAt = now;
      }
      if (key === "speed" && next !== p.speed) {
        lastHoistChangeAt = now;
      }
      const nextParams = { ...p, [key]: next } as CraneParameters;
      return {
        craneParameters: nextParams,
        lastSlewChangeAt,
        lastBoomChangeAt,
        lastHoistChangeAt,
        currentAction: deriveCurrentAction(nextParams, {
          lastSlewChangeAt,
          lastBoomChangeAt,
          lastHoistChangeAt,
        }),
      };
    }),

  decrementCraneParameter: <K extends keyof CraneParameters>(
    key: K,
    amount: number = 1
  ) =>
    set((state: any) => {
      const p = state.craneParameters;
      let next = (p[key] as any) - amount;
      if (key === "boomAngle") {
        next = Math.max(p.boomMinAngle, Math.min(p.boomMaxAngle, next));
      }
      if (key === "slewAngle") {
        next = Math.max(p.slewMinAngle, Math.min(p.slewMaxAngle, next));
      }
      if (key === "speed") {
        next = Math.max(p.speedMin, Math.min(p.speedMax, next));
      }
      if (key === "boomSpeed") {
        next = Math.max(p.speedMin, Math.min(p.speedMax, next));
      }
      if (key === "slewSpeed") {
        next = Math.max(p.speedMin, Math.min(p.speedMax, next));
      }
      if (key === "absTravel") {
        const nextBlock = computeBlockPosition(next);

        // Adjust speed limits based on block position
        let nextSpeedMin = p.speedMin;
        let nextSpeedMax = p.speedMax;
        if (nextBlock === BLOCK_POSITION.HIGH) {
          nextSpeedMin = DEFAULT_SPEED_MIN;
          nextSpeedMax = 0;
        } else if (nextBlock === BLOCK_POSITION.PRE_HIGH) {
          nextSpeedMin = PRE_HIGH_SPEED_MIN;
          nextSpeedMax = PRE_HIGH_SPEED_MAX;
        } else {
          nextSpeedMin = DEFAULT_SPEED_MIN;
          nextSpeedMax = DEFAULT_SPEED_MAX;
        }
        let nextSpeed = Math.max(nextSpeedMin, Math.min(nextSpeedMax, p.speed));

        try {
          const { addAlert, removeAlert, alerts } =
            (state as any).get?.() ?? get();
          const removeByDefinition = (def: AlertDefinitionKey) => {
            alerts
              .filter((a: any) => a.definitionId === def)
              .forEach((a: any) => removeAlert(a.id));
          };
          const ensurePresent = (def: AlertDefinitionKey) => {
            const exists = alerts.some((a: any) => a.definitionId === def);
            if (!exists) {
              const base = createAlertFromDefinition(def);
              addAlert({
                id: `alert-${Date.now()}-${Math.random()
                  .toString(36)
                  .slice(2)}`,
                ...base,
              });
            }
          };

          if (nextBlock === BLOCK_POSITION.HIGH) {
            ensurePresent(AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_HIGH);
            removeByDefinition(
              AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_PRE_HIGH
            );
          } else if (nextBlock === BLOCK_POSITION.PRE_HIGH) {
            ensurePresent(
              AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_PRE_HIGH
            );
            removeByDefinition(
              AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_HIGH
            );
          } else {
            removeByDefinition(
              AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_HIGH
            );
            removeByDefinition(
              AlertDefinitionKey.MAIN_HOIST_BLOCK_POSITION_PRE_HIGH
            );
          }
        } catch {}

        return {
          craneParameters: {
            ...p,
            absTravel: next,
            blockPosition: nextBlock,
            speedMin: nextSpeedMin,
            speedMax: nextSpeedMax,
            speed: nextSpeed,
          },
          currentAction: deriveCurrentAction(
            {
              ...p,
              absTravel: next,
              blockPosition: nextBlock,
              speedMin: nextSpeedMin,
              speedMax: nextSpeedMax,
              speed: nextSpeed,
            },
            {
              lastSlewChangeAt: state.lastSlewChangeAt,
              lastBoomChangeAt: state.lastBoomChangeAt,
              lastHoistChangeAt: state.lastHoistChangeAt,
            }
          ),
        };
      }
      const now = Date.now();
      let lastSlewChangeAt = state.lastSlewChangeAt;
      let lastBoomChangeAt = state.lastBoomChangeAt;
      let lastHoistChangeAt = state.lastHoistChangeAt;
      if (key === "slewSpeed" && next !== p.slewSpeed) {
        lastSlewChangeAt = now;
      }
      if (key === "boomSpeed" && next !== p.boomSpeed) {
        lastBoomChangeAt = now;
      }
      if (key === "speed" && next !== p.speed) {
        lastHoistChangeAt = now;
      }
      const nextParams = { ...p, [key]: next } as CraneParameters;
      return {
        craneParameters: nextParams,
        lastSlewChangeAt,
        lastBoomChangeAt,
        lastHoistChangeAt,
        currentAction: deriveCurrentAction(nextParams, {
          lastSlewChangeAt,
          lastBoomChangeAt,
          lastHoistChangeAt,
        }),
      };
    }),
});
